import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { Database } from 'supabase/database.types'
import { queryKeys } from './queryKeys'

export type EmailThread_ = Database['public']['Tables']['email_thread']['Row']
export type EmailMessage_ = Database['public']['Tables']['email_message']['Row']
export type InboxItem_ = Database['public']['Tables']['inbox_item']['Row']
export type SuggestedReply_ =
  Database['public']['Tables']['suggested_reply']['Row']
export type EmailContact_ = Database['public']['Tables']['email_contact']['Row']

export type InboxStatusFilter =
  | 'needs_action'
  | 'waiting'
  | 'done'
  | 'ignored'
  | 'all'

export type InboxListRow = InboxItem_ & {
  suggested_reply: Array<
    Pick<SuggestedReply_, 'id' | 'body_text' | 'status' | 'kind'>
  > | null
  email_thread: EmailThread_ & {
    email_contact: EmailContact_ | null
    organization: { id: string; name: string } | null
    quote: { id: number; public_id: string | null } | null
  } | null
}

export const useEmailInboxList = ({
  status,
  functionTag,
  search,
  mailboxEmail,
}: {
  status: InboxStatusFilter
  functionTag: string | null
  search: string
  mailboxEmail?: string | null
}) => {
  const supabase = useSupabaseClient()

  return useQuery({
    queryKey: [queryKeys.emailInbox, status, functionTag, search, mailboxEmail],
    queryFn: async (): Promise<InboxListRow[]> => {
      let query = supabase
        .from('inbox_item')
        .select(
          `
          *,
          suggested_reply (id, body_text, status, kind),
          email_thread (
            *,
            email_contact (*),
            organization (id, name),
            quote (id, public_id)
          )
        `
        )
        .order('updated_at', { ascending: false })
        .limit(200)

      if (status === 'needs_action') {
        query = query.in('status', ['needs_draft', 'needs_review'])
      } else if (status === 'waiting') {
        query = query.eq('status', 'waiting')
      } else if (status === 'done') {
        query = query.eq('status', 'done')
      } else if (status === 'ignored') {
        query = query.eq('status', 'ignored')
      }

      if (functionTag) {
        query = query.eq('function_tag', functionTag)
      }

      const { data, error } = await query
      if (error) throw error

      let rows = (data ?? []) as InboxListRow[]
      if (mailboxEmail) {
        rows = rows.filter(
          (row) => row.email_thread?.mailbox_email === mailboxEmail
        )
      }
      const q = search.trim().toLowerCase()
      if (!q) return rows
      return rows.filter((row) => {
        const thread = row.email_thread
        const hay = [
          row.summary,
          thread?.subject,
          thread?.snippet,
          thread?.email_contact?.email,
          thread?.email_contact?.name,
          thread?.organization?.name,
          thread?.mailbox_email,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
        return hay.includes(q)
      })
    },
  })
}

export const useMailboxAccounts = () => {
  const supabase = useSupabaseClient()
  return useQuery({
    queryKey: [queryKeys.emailInbox, 'mailboxes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('mailbox_account')
        .select('id, email, display_name')
        .eq('is_active', true)
        .order('email')
      if (error) throw error
      return data ?? []
    },
  })
}

export const useEmailThreadDetail = (threadId: string | null) => {
  const supabase = useSupabaseClient()

  return useQuery({
    queryKey: [queryKeys.emailInbox, 'thread', threadId],
    enabled: Boolean(threadId),
    queryFn: async () => {
      if (!threadId) return null
      const [{ data: thread, error: threadError }, { data: messages, error: msgError }, { data: item, error: itemError }] =
        await Promise.all([
          supabase
            .from('email_thread')
            .select(
              `
              *,
              email_contact (*),
              organization (id, name),
              quote (id, public_id)
            `
            )
            .eq('id', threadId)
            .single(),
          supabase
            .from('email_message')
            .select('*')
            .eq('thread_id', threadId)
            .order('sent_at', { ascending: true }),
          supabase
            .from('inbox_item')
            .select('*')
            .eq('thread_id', threadId)
            .maybeSingle(),
        ])
      if (threadError) throw threadError
      if (msgError) throw msgError
      if (itemError) throw itemError

      let drafts: SuggestedReply_[] = []
      if (item?.id) {
        const { data, error } = await supabase
          .from('suggested_reply')
          .select('*')
          .eq('inbox_item_id', item.id)
          .order('created_at', { ascending: true })
        if (error) throw error
        drafts = data ?? []
      }

      return {
        thread: thread as EmailThread_ & {
          email_contact: EmailContact_ | null
          organization: { id: string; name: string } | null
          quote: { id: number; public_id: string | null } | null
        },
        messages: (messages ?? []) as EmailMessage_[],
        item: item as InboxItem_ | null,
        drafts,
      }
    },
  })
}

export const useUpdateInboxItem = () => {
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ...values
    }: { id: string } & Database['public']['Tables']['inbox_item']['Update']) => {
      const { error } = await supabase
        .from('inbox_item')
        .update(values)
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKeys.emailInbox] })
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.emailInboxStatus],
      })
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.emailInboxInsights],
      })
    },
  })
}

export const useUpdateSuggestedReply = () => {
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      ...values
    }: { id: string } & Database['public']['Tables']['suggested_reply']['Update']) => {
      const { error } = await supabase
        .from('suggested_reply')
        .update(values)
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKeys.emailInbox] })
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.emailInboxStatus],
      })
      void queryClient.invalidateQueries({
        queryKey: [queryKeys.emailInboxInsights],
      })
    },
  })
}
